import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input, Textarea } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, type Column } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { useVolver } from '../../lib/useVolver';
import { toast } from '../../lib/toast';
import { GameCalculadora } from '../game/GameCalculadora';
import { ProductoArchivos, ProductoFotos } from '../../components/ProductoArchivos';
import { ProductoScrapeModal } from '../../components/ProductoScrapeModal';
import { CosteEntradaFicha } from '../../components/CosteEntradaFicha';
import { VentasApproxFicha } from '../../components/VentasApproxFicha';
import { EntregasProductoSection, HistoricoPrecioVentaSection } from '../../components/producto/PedidosEntregas';
import { ProductoProveedorDetalleView } from '../catalogo/ProductoProveedorDetalle';

type StockRow = {
  id: string;
  fecha: string;
  stock_actual: number | null;
  stock_df: number | null;
  unidades_vendidas: number | null;
  origen: string;
};

type PedidoRow = {
  id: string;
  fecha: string;
  unidades: number;
  precio_compra_usd: number | null;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  ref_externa: string | null;
  fecha_estimada_llegada: string | null;
  fecha_recepcion: string | null;
};

type CotizacionRow = {
  id: string;
  fecha: string;
  unidades: number | null;
  coste_usd: number | string | null;
  cambio_usd_eur: number | string | null;
  precio_contenedor_eur: number | string | null;
  cdpe_eur: number | string | null;
  coste_real_eur: number | string | null;
  coste_game_final_eur: number | string | null;
  pvpr_iva_eur: number | string | null;
  ref_proveedor: string | null;
  notas: string | null;
};

type Metricas = {
  stock_actual: number | null;
  stock_tiendas: number | null;
  stock_fecha: string | null;
  ventas_l4wk: number;
  ventas_l12wk: number;
  media_semanal_l4: number;
  semanas_stock: number | null;
  uds_pedidas: number;
  ultima_entrega_fecha: string | null;
  ultimo_coste: number | null;
  ultimo_coste_fecha: string | null;
};

type ProductoFull = {
  id: string;
  sku: string;
  ean: string | null;
  nombre: string;
  marca_id: string;
  marca_nombre: string | null;
  cliente_id: string;
  cliente_nombre: string | null;
  cliente_codigo: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  detalle_id: string | null;
  detalle_nombre: string | null;
  proveedor_id: string | null;
  proveedor_nombre: string | null;
  producto_proveedor_id: string | null;
  ref_proveedor: string | null;
  part_number: string | null;
  coste: number | null;
  pvpr: number | null;
  precio_target: number | null;
  margen_pct: number | null;
  revision_pendiente: boolean;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  fecha_creacion: string;
  notas: string | null;
  familia: string | null;
  fecha_lanzamiento: string | null;
  ficha_descripcion: string | null;
  gama_id: string | null;
  gama_codigo: string | null;
  gama_nombre: string | null;
  url_oficial: string | null;
  // Sustitución de referencias. `sustituido_por_*` mira hacia adelante (esta
  // ficha es la vieja); `sustituye_a` es el lookup inverso (esta es la nueva) y
  // es lista porque varias referencias pueden refundirse en una sola.
  sustituido_por_id: string | null;
  sustituido_por_sku: string | null;
  sustituido_por_nombre: string | null;
  sustituye_a: { id: string; sku: string; nombre: string }[];
  stock: StockRow[];
  pedidos: PedidoRow[];
  cotizaciones: CotizacionRow[];
  historico_semanal: HistoricoSemanalRow[];
  metricas: Metricas;
};

type HistoricoSemanalRow = {
  fecha_informe: string;
  semana_inicio: string;
  semana_fin: string;
  ano_fiscal_game: number | null;
  semana_fiscal_game: number | null;
  unidades_vendidas: number | null;
  stock_actual: number | null;
};

// Margen comercial del cliente: el coste (lo que paga a Approx) va SIN IVA y el
// PVPr es CON IVA (21%). Para que el margen sea correcto comparamos contra el
// PVPr SIN IVA: (PVPr_sin_iva − coste) / PVPr_sin_iva.
// Estados en los que la referencia está de salida y procede apuntar quién la
// reemplaza. BUSCAR_SUSTITUTO es el paso previo: aún se está buscando, pero en
// cuanto aparece el candidato se anota sin esperar a descatalogar.
const ESTADOS_CON_SUSTITUTO = ['DESCATALOGADO', 'BUSCAR_SUSTITUTO'];

const IVA_GENERAL_PCT = 21;
function calcMargen(coste: number | null, pvpr: number | null): number | null {
  if (coste == null || pvpr == null) return null;
  const c = Number(coste);
  const pConIva = Number(pvpr);
  if (!Number.isFinite(c) || !Number.isFinite(pConIva) || pConIva <= 0) return null;
  const p = pConIva / (1 + IVA_GENERAL_PCT / 100);
  if (p <= 0) return null;
  return Number((((p - c) / p) * 100).toFixed(2));
}

export function ProductoDetalle() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const qc = useQueryClient();
  const navigate = useNavigate();

  const producto = useQuery({
    queryKey: ['producto', id, 'full'],
    queryFn: () => api.get<ProductoFull>(`/productos/${id}/full`),
    enabled: !!id,
  });

  const estados = useQuery({
    queryKey: ['list', '/admin/producto-estados'],
    queryFn: () => api.get<{ data: { codigo: string; nombre: string; color: string }[] }>('/admin/producto-estados'),
  });

  const gamas = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/gamas'),
  });
  const detalles = useQuery({
    queryKey: ['list', '/admin/detalles'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; subcategoria_id: string; activo: boolean }[] }>('/admin/detalles'),
  });
  const proveedores = useQuery({
    queryKey: ['list', '/proveedores'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/proveedores'),
  });

  const updateProducto = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<ProductoFull>(`/productos/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto', id, 'full'] });
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
    },
  });

  // Vincula/crea la ficha de proveedor asociada (productos_proveedor) y la
  // enlaza por producto_proveedor_id. proveedor_id null = desvincular.
  const vincularProveedor = useMutation({
    mutationFn: (body: { proveedor_id: string | null; ref_proveedor?: string }) =>
      api.post<{ producto_proveedor_id: string | null }>(`/productos/${id}/vincular-proveedor`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto', id, 'full'] });
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
    },
  });

  // Fija el producto que reemplaza a este resolviendo por SKU en el servidor
  // (que valida existencia, cliente y ciclos). sku null = deshacer.
  const fijarSustituto = useMutation({
    mutationFn: (body: { sku: string | null }) =>
      api.put<{ sustituido_por_id: string | null }>(`/productos/${id}/sustituto`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto', id, 'full'] });
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
    },
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<ProductoFull>>({});
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [tab, setTab] = useState<'cliente' | 'cotizaciones' | 'stock' | 'proveedor'>('cliente');
  const [recalcCotizId, setRecalcCotizId] = useState<string | null>(null);

  // Fallback dinámico de "Volver" según el cliente del producto, evaluado al
  // pulsar el botón (no en montaje). Si hay historia, el hook usa navigate(-1).
  const volver = useVolver(() => {
    const cli = producto.data?.cliente_codigo;
    // Base por cliente derivada de cliente_codigo (multi-cliente, sin hardcodear).
    return cli ? `/${cli.toLowerCase()}/productos` : '/';
  });

  // Auto-recalcular margen cuando coste/pvpr cambian en el editor.
  useEffect(() => {
    const auto = calcMargen(
      typeof draft.coste === 'number' ? draft.coste : null,
      typeof draft.pvpr === 'number' ? draft.pvpr : null,
    );
    setDraft((d) => (d.margen_pct === auto ? d : { ...d, margen_pct: auto }));
  }, [draft.coste, draft.pvpr]);

  // Enlace perezoso de la ficha de proveedor: si el producto tiene proveedor
  // asignado pero aún no existe el enlace (p. ej. proveedor puesto desde los
  // cambios masivos del listado, una importación o datos previos), al abrir la
  // pestaña "Datos proveedor" creamos/enlazamos su ficha automáticamente.
  const autolinkRef = useRef(false);
  useEffect(() => {
    const pd = producto.data;
    if (tab !== 'proveedor' || !pd) return;
    if (pd.producto_proveedor_id || !pd.proveedor_id) return;
    if (autolinkRef.current || vincularProveedor.isPending) return;
    autolinkRef.current = true;
    vincularProveedor.mutate({ proveedor_id: pd.proveedor_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, producto.data]);

  if (producto.isLoading) return <Spinner />;
  if (producto.isError || !producto.data) {
    return (
      <div>
        <PageHeader title="Producto no encontrado" />
        <Button variant="secondary" onClick={volver}>← Volver al listado</Button>
      </div>
    );
  }
  const p = producto.data;
  // Nombre del cliente del producto, para textos (no hardcodear GAME).
  const clienteCodigo = p.cliente_codigo ?? 'cliente';

  // Ficha de otro producto DEL MISMO CLIENTE (el servidor garantiza que el
  // sustituto lo es), derivando la base de ruta del cliente sin hardcodearlo.
  const productoHref = (otroId: string) =>
    `/${(p.cliente_codigo ?? 'game').toLowerCase()}/productos/${otroId}`;

  const openEdit = () => {
    setDraft({
      sku: p.sku,
      ean: p.ean ?? '',
      nombre: p.nombre,
      proveedor_id: p.proveedor_id ?? '',
      ref_proveedor: p.ref_proveedor ?? '',
      part_number: p.part_number ?? '',
      // Numéricos de Postgres llegan como string; coercionar para que el margen
      // se autocalcule de inmediato (typeof === 'number') sin tener que editar.
      coste: p.coste != null ? Number(p.coste) : null,
      pvpr: p.pvpr != null ? Number(p.pvpr) : null,
      precio_target: p.precio_target != null ? Number(p.precio_target) : null,
      margen_pct: p.margen_pct != null ? Number(p.margen_pct) : null,
      revision_pendiente: p.revision_pendiente,
      estado: p.estado,
      notas: p.notas ?? '',
      familia: p.familia ?? '',
      fecha_lanzamiento: p.fecha_lanzamiento ?? '',
      ficha_descripcion: p.ficha_descripcion ?? '',
      gama_id: p.gama_id ?? '',
      categoria_id: p.categoria_id ?? '',
      subcategoria_id: p.subcategoria_id ?? '',
      detalle_id: p.detalle_id ?? '',
      url_oficial: p.url_oficial ?? '',
      // Se edita por SKU y lo resuelve el servidor: no viaja en el PUT genérico.
      sustituido_por_sku: p.sustituido_por_sku ?? '',
    });
    setEditing(true);
  };

  const submitEdit = async () => {
    // Limpia strings vacíos a null (PG no acepta "" para uuids/dates) y
    // descarta el margen calculado si quedó NaN.
    const body: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(draft)) {
      // El sustituto se resuelve por SKU en su propio endpoint, más abajo.
      if (key === 'sustituido_por_sku') continue;
      if (value === '') body[key] = null;
      else if (key === 'margen_pct' && typeof value === 'number' && !Number.isFinite(value)) continue;
      else body[key] = value;
    }
    await updateProducto.mutateAsync(body);

    // Sustituto: solo se toca si cambió el SKU tecleado. Si el servidor lo
    // rechaza (no existe, otro cliente, ciclo) se avisa sin deshacer el resto
    // del guardado, que ya es válido de por sí.
    const sustitutoAntes = p.sustituido_por_sku ?? '';
    const sustitutoAhora = ((draft.sustituido_por_sku as string | undefined) ?? '').trim();
    if (sustitutoAhora.toUpperCase() !== sustitutoAntes.toUpperCase()) {
      try {
        await fijarSustituto.mutateAsync({ sku: sustitutoAhora || null });
      } catch (err) {
        const msg = (err as Error).message;
        toast.warning('Guardado con avisos', `El producto se guardó, pero no se pudo fijar el sustituto:\n${msg}`);
      }
    }

    // Si cambió el proveedor, garantizamos/actualizamos su ficha de proveedor
    // y el enlace (producto_proveedor_id) para que la pestaña "Datos proveedor"
    // edite el mismo registro.
    const proveedorAntes = p.proveedor_id ?? '';
    const proveedorAhora = (draft.proveedor_id as string | undefined) ?? '';
    if (proveedorAhora !== proveedorAntes) {
      await vincularProveedor.mutateAsync({
        proveedor_id: proveedorAhora || null,
        ref_proveedor: (draft.ref_proveedor as string | undefined)?.trim() || undefined,
      });
    }
    setEditing(false);
  };

  const cancelEdit = () => {
    setDraft({});
    setEditing(false);
  };

  const stockCols: Column<StockRow>[] = [
    { key: 'fecha', label: 'Fecha', render: (r) => fmt.date(r.fecha), sortValue: (r) => r.fecha },
    { key: 'stock_actual', label: 'Actual', align: 'right', render: (r) => fmt.number(r.stock_actual), sortValue: (r) => Number(r.stock_actual) },
    { key: 'stock_df', label: 'DF', align: 'right', render: (r) => fmt.number(r.stock_df), sortValue: (r) => Number(r.stock_df) },
    { key: 'unidades_vendidas', label: 'Vendidas', align: 'right', render: (r) => fmt.number(r.unidades_vendidas), sortValue: (r) => Number(r.unidades_vendidas) },
    { key: 'origen', label: 'Origen', sortValue: (r) => r.origen },
  ];

  const pedidoCols: Column<PedidoRow>[] = [
    { key: 'fecha', label: 'Fecha', render: (r) => fmt.date(r.fecha), sortValue: (r) => r.fecha },
    { key: 'unidades', label: 'Unidades', align: 'right', render: (r) => fmt.number(r.unidades), sortValue: (r) => Number(r.unidades) },
    { key: 'precio_compra_usd', label: 'Coste USD', align: 'right', render: (r) => fmt.usd(r.precio_compra_usd), sortValue: (r) => Number(r.precio_compra_usd) },
    { key: 'ref_externa', label: 'Ref. externa', sortValue: (r) => r.ref_externa ?? '' },
    { key: 'estado', label: 'Estado', render: (r) => <Badge color={r.estado_color ?? '#64748b'}>{r.estado_nombre ?? r.estado}</Badge>, sortValue: (r) => r.estado_nombre ?? r.estado },
    { key: 'fecha_estimada_llegada', label: 'ETA', render: (r) => fmt.date(r.fecha_estimada_llegada), sortValue: (r) => r.fecha_estimada_llegada ?? '' },
  ];

  const cotizCols: Column<CotizacionRow>[] = [
    { key: 'fecha', label: 'Fecha', render: (r) => fmt.datetime(r.fecha), sortValue: (r) => r.fecha },
    { key: 'unidades', label: 'Uds.', align: 'right', render: (r) => r.unidades != null ? fmt.int(r.unidades) : '—', sortValue: (r) => Number(r.unidades) },
    { key: 'coste_usd', label: 'Coste USD', align: 'right', render: (r) => fmt.usd(r.coste_usd), sortValue: (r) => Number(r.coste_usd) },
    { key: 'cambio_usd_eur', label: 'Cambio', align: 'right', render: (r) => fmt.number(r.cambio_usd_eur), sortValue: (r) => Number(r.cambio_usd_eur) },
    { key: 'precio_contenedor_eur', label: 'Contenedor', align: 'right', render: (r) => fmt.eur(r.precio_contenedor_eur), sortValue: (r) => Number(r.precio_contenedor_eur) },
    { key: 'cdpe_eur', label: 'CDPe', align: 'right', render: (r) => fmt.eur(r.cdpe_eur), sortValue: (r) => Number(r.cdpe_eur) },
    { key: 'coste_real_eur', label: 'Coste real', align: 'right', render: (r) => fmt.eur(r.coste_real_eur), sortValue: (r) => Number(r.coste_real_eur) },
    { key: 'coste_game_final_eur', label: 'Coste final', align: 'right', render: (r) => fmt.eur(r.coste_game_final_eur), sortValue: (r) => Number(r.coste_game_final_eur) },
    { key: 'pvpr_iva_eur', label: 'PVPr c/IVA', align: 'right', render: (r) => fmt.eur(r.pvpr_iva_eur), sortValue: (r) => Number(r.pvpr_iva_eur) },
    { key: 'notas', label: 'Notas', sortValue: (r) => r.notas ?? '' },
    {
      key: 'recalc',
      label: '',
      sortable: false,
      align: 'right',
      render: (r) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setRecalcCotizId(r.id); }}
          className="cursor-pointer whitespace-nowrap rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
          title="Recalcular con los datos vigentes y duplicar en una cotización nueva."
        >
          ↻ Actualizar
        </button>
      ),
    },
  ];

  const metricas = p.metricas;

  return (
    <div>
      <PageHeader
        title={p.nombre}
        subtitle={`${p.sku}${p.ean ? ` · EAN ${p.ean}` : ''} · ${p.marca_nombre ?? '—'} · ${p.cliente_nombre ?? '—'}`}
        actions={
          <>
            <Button variant="secondary" onClick={volver}>← Volver</Button>
            {tab === 'cliente' && (
              <>
                {!editing && (
                  <Button
                    variant="secondary"
                    onClick={() => setScrapeOpen(true)}
                    title={p.url_oficial
                      ? `Lanza scraping a ${p.url_oficial} y trae nombre, descripción, ficha técnica y fotos.`
                      : 'Antes pulsa Editar y rellena el campo "URL oficial" para que sepamos qué URL scrapear.'}
                  >
                    🌐 Importar de web
                  </Button>
                )}
                {editing ? (
                  <>
                    <Button variant="danger" onClick={cancelEdit}>Cancelar</Button>
                    <Button variant="save" onClick={submitEdit} disabled={updateProducto.isPending || vincularProveedor.isPending}>
                      {updateProducto.isPending || vincularProveedor.isPending ? 'Guardando…' : 'Guardar'}
                    </Button>
                  </>
                ) : (
                  <Button variant="edit" onClick={openEdit}>✏️ Editar</Button>
                )}
              </>
            )}
          </>
        }
      />

      {/* Pestañas de la ficha. */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
        {([
          ['cliente', `Datos ${clienteCodigo}`],
          ['cotizaciones', 'Cotizaciones'],
          ['stock', 'Stock y ventas'],
          ['proveedor', 'Datos proveedor'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px cursor-pointer border-b-2 px-4 py-2 text-sm font-medium ${
              tab === key ? 'border-sky-600 text-sky-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'proveedor' && (
        p.producto_proveedor_id ? (
          <ProductoProveedorDetalleView id={p.producto_proveedor_id} embedded />
        ) : p.proveedor_id ? (
          <Card>
            <div className="py-6 text-center text-sm text-slate-500">
              {vincularProveedor.isError ? (
                <>
                  <p className="mb-2 font-medium text-rose-600">No se pudo crear la ficha de proveedor.</p>
                  <Button
                    variant="secondary"
                    onClick={() => { autolinkRef.current = false; vincularProveedor.mutate({ proveedor_id: p.proveedor_id! }); }}
                  >
                    Reintentar
                  </Button>
                </>
              ) : (
                <p>Vinculando la ficha de proveedor de <span className="font-medium text-slate-700">{p.proveedor_nombre ?? 'el proveedor'}</span>…</p>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="py-6 text-center text-sm text-slate-500">
              <p className="mb-1 font-medium text-slate-700">Este producto no tiene proveedor asignado.</p>
              <p>
                Ve a la pestaña «Datos {clienteCodigo}», pulsa <span className="font-medium">Editar</span> y elige un
                proveedor. Al guardar se creará (o enlazará) su ficha aquí y en la sección Proveedores.
              </p>
            </div>
          </Card>
        )
      )}

      {tab === 'cliente' && (
      <>
      {p.revision_pendiente && !editing && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="text-base">⚠️</span>
          <div className="flex-1">
            <p className="font-semibold">Ficha marcada como revisión pendiente.</p>
            <p className="text-xs text-amber-800">
              El producto se creó automáticamente (importación o aceptación de cotización). Revisa coste, PVPr, categoría y proveedor; luego desmarca el flag pulsando «Editar» en esta ficha.
            </p>
          </div>
        </div>
      )}

      {/* Sustitución de referencia — se pinta en los dos sentidos para poder
          saltar de la ficha vieja a la nueva y al revés. Visible también en
          edición: es contexto, no un campo. */}
      {p.sustituido_por_id && p.sustituido_por_sku && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-900">
          <span className="text-base">🔄</span>
          <div className="flex-1">
            <p>
              Referencia sustituida por SKU:{' '}
              <button
                type="button"
                onClick={() => navigate(productoHref(p.sustituido_por_id!))}
                className="cursor-pointer font-semibold underline underline-offset-2 hover:text-orange-950"
              >
                {p.sustituido_por_sku}
              </button>
            </p>
            {p.sustituido_por_nombre && (
              <p className="text-xs text-orange-800">{p.sustituido_por_nombre}</p>
            )}
          </div>
        </div>
      )}

      {p.sustituye_a?.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          <span className="text-base">🔄</span>
          <div className="flex-1">
            <p>
              Este producto sustituye al SKU:{' '}
              {p.sustituye_a.map((s, i) => (
                <span key={s.id}>
                  {i > 0 && ', '}
                  <button
                    type="button"
                    onClick={() => navigate(productoHref(s.id))}
                    className="cursor-pointer font-semibold underline underline-offset-2 hover:text-emerald-950"
                  >
                    {s.sku}
                  </button>
                </span>
              ))}
            </p>
            {p.sustituye_a.length === 1 && (
              <p className="text-xs text-emerald-800">{p.sustituye_a[0].nombre}</p>
            )}
          </div>
        </div>
      )}

      {/* ── Identidad: 4 filas (nombre+flag / identificadores / taxonomía /
          económicos+URL). Diseño pedido por el CEO 2026-06-03. Edición INLINE:
          al pulsar «Editar» cada celda se convierte en input/select. ── */}
      <Card className="mb-4">
        {/* Fila 1: Nombre + revisión pendiente */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          {editing ? (
            <Input
              className="min-w-0 flex-1 text-lg font-semibold"
              value={(draft.nombre as string) ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))}
              placeholder="Nombre del producto"
            />
          ) : (
            <h2 className="min-w-0 break-words text-xl font-semibold text-slate-900">{p.nombre}</h2>
          )}
          <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 text-sm" title="Marca/desmarca el flag de revisión pendiente.">
            <input
              type="checkbox"
              checked={editing ? Boolean(draft.revision_pendiente) : p.revision_pendiente}
              onChange={(e) => editing && setDraft((d) => ({ ...d, revision_pendiente: e.target.checked }))}
              readOnly={!editing}
              disabled={!editing}
              className="h-4 w-4 rounded border-slate-300"
            />
            <span className={(editing ? draft.revision_pendiente : p.revision_pendiente) ? 'font-medium text-amber-700' : 'text-slate-500'}>
              Revisión pendiente
            </span>
          </label>
        </div>

        {/* Fila 2: identificadores */}
        <div className="mt-3 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
          <FieldCol label="SKU">
            {editing
              ? <Input className="font-mono" value={(draft.sku as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, sku: e.target.value }))} />
              : <span className="font-mono">{p.sku}</span>}
          </FieldCol>
          <FieldCol label="EAN">
            {editing
              ? <Input className="font-mono" value={(draft.ean as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, ean: e.target.value }))} />
              : <span className="font-mono">{p.ean ?? '—'}</span>}
          </FieldCol>
          <FieldCol label="Part Number" hint="Referencia del fabricante. Independiente de SKU, EAN y ref. proveedor.">
            {editing
              ? <Input className="font-mono" value={(draft.part_number as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, part_number: e.target.value }))} />
              : <span className="font-mono">{p.part_number ?? '—'}</span>}
          </FieldCol>
          <FieldCol label="Proveedor" hint={editing ? 'Al asignar un proveedor se crea/enlaza su ficha en la sección Proveedores (pestaña «Datos proveedor»).' : undefined}>
            {editing ? (
              <Select
                options={(proveedores.data?.data ?? []).map((pv) => ({ value: pv.id, label: pv.nombre }))}
                value={(draft.proveedor_id as string) ?? ''}
                emptyLabel="— sin proveedor —"
                onChange={(e) => setDraft((d) => ({ ...d, proveedor_id: e.target.value }))}
              />
            ) : (p.proveedor_nombre ?? '—')}
          </FieldCol>
          <FieldCol label="Ref. proveedor">
            {editing
              ? <Input className="font-mono" value={(draft.ref_proveedor as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, ref_proveedor: e.target.value }))} />
              : <span className="font-mono">{p.ref_proveedor ?? '—'}</span>}
          </FieldCol>
        </div>

        {/* Fila 3: taxonomía */}
        <div className="mt-3 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
          <FieldCol label="Marca">{p.marca_nombre ?? '—'}</FieldCol>
          <FieldCol label="Gama">
            {editing ? (
              <Select
                options={(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))}
                value={(draft.gama_id as string) ?? ''}
                emptyLabel="—"
                onChange={(e) => setDraft((d) => ({ ...d, gama_id: e.target.value }))}
              />
            ) : (p.gama_nombre ?? '—')}
          </FieldCol>
          <FieldCol label="Categoría">{p.categoria_nombre ?? '—'}</FieldCol>
          <FieldCol label="Subcategoría">{p.subcategoria_nombre ?? '—'}</FieldCol>
          <FieldCol label="Detalle" hint="Cuarta capa: cuelga de la subcategoría del producto.">
            {editing ? (
              <Select
                options={(detalles.data?.data ?? [])
                  .filter((d) => d.activo && d.subcategoria_id === (draft.subcategoria_id ?? p.subcategoria_id))
                  .map((d) => ({ value: d.id, label: d.nombre }))}
                value={(draft.detalle_id as string) ?? ''}
                emptyLabel="— sin detalle —"
                onChange={(e) => setDraft((d) => ({ ...d, detalle_id: e.target.value }))}
                disabled={!(draft.subcategoria_id ?? p.subcategoria_id)}
              />
            ) : (p.detalle_nombre ?? '—')}
          </FieldCol>
        </div>

        {/* Fila 4: económicos + URL (+ estado para no perderlo) */}
        <div className="mt-3 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
          <FieldCol label="Coste (EUR)">
            {editing
              ? <Input type="number" step="any" value={(draft.coste as number | null) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, coste: e.target.value === '' ? null : Number(e.target.value) }))} />
              : fmt.eur(p.coste)}
          </FieldCol>
          <FieldCol label="PVPr (EUR)">
            {editing
              ? <Input type="number" step="any" value={(draft.pvpr as number | null) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, pvpr: e.target.value === '' ? null : Number(e.target.value) }))} />
              : fmt.eur(p.pvpr)}
          </FieldCol>
          <FieldCol label="Margen" hint="Autocalculado: (PVPr sin IVA − Coste) / PVPr sin IVA. No editable.">
            {editing
              ? <span className="font-semibold">{fmt.pct(calcMargen(typeof draft.coste === 'number' ? draft.coste : null, typeof draft.pvpr === 'number' ? draft.pvpr : null))}</span>
              : fmt.pct(calcMargen(p.coste, p.pvpr))}
          </FieldCol>
          <FieldCol label="URL oficial" hint={editing ? (clienteCodigo === 'GAME' ? 'Para GAME por defecto: https://www.game.es/<sku>.' : 'URL de la ficha oficial del producto en la web del cliente.') : undefined}>
            {editing
              ? <Input value={(draft.url_oficial as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, url_oficial: e.target.value }))} placeholder="https://…" />
              : (p.url_oficial
                ? <a href={p.url_oficial} target="_blank" rel="noreferrer" className="cursor-pointer text-sky-600 hover:underline">Abrir ↗</a>
                : <span className="text-slate-400">—</span>)}
          </FieldCol>
          <FieldCol label="Estado">
            {editing ? (
              <Select
                options={(estados.data?.data ?? []).map((e) => ({ value: e.codigo, label: e.nombre }))}
                value={(draft.estado as string) ?? ''}
                emptyLabel={null}
                onChange={(e) => setDraft((d) => ({ ...d, estado: e.target.value }))}
              />
            ) : <Badge color={p.estado_color ?? '#64748b'}>{p.estado_nombre ?? p.estado}</Badge>}
          </FieldCol>
          {/* El sustituto solo tiene sentido en las referencias que se retiran,
              así que el campo aparece con los estados de salida. */}
          {editing && ESTADOS_CON_SUSTITUTO.includes((draft.estado as string) ?? '') && (
            <FieldCol label="Sustituido por (SKU)" hint="SKU del producto que reemplaza a este. Debe ser del mismo cliente. Vacío = sin sustituto.">
              <Input
                value={(draft.sustituido_por_sku as string) ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, sustituido_por_sku: e.target.value }))}
                placeholder="p. ej. NW3866"
              />
            </FieldCol>
          )}
        </div>

        {/* Fila extra (solo edición): familia, fecha lanzamiento, target + notas */}
        {editing && (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
              <FieldCol label="Familia">
                <Input value={(draft.familia as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, familia: e.target.value }))} />
              </FieldCol>
              <FieldCol label="Fecha lanzamiento">
                <Input type="date" value={(draft.fecha_lanzamiento as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, fecha_lanzamiento: e.target.value }))} />
              </FieldCol>
              <FieldCol label="Target (EUR)" hint="Precio objetivo opcional, fijado a mano. No interviene en cálculos.">
                <Input type="number" step="any" value={(draft.precio_target as number | null) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, precio_target: e.target.value === '' ? null : Number(e.target.value) }))} />
              </FieldCol>
            </div>
            <div className="mt-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Notas internas</div>
              <Textarea className="mt-0.5" value={(draft.notas as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, notas: e.target.value }))} />
            </div>
          </div>
        )}
      </Card>

      {/* ── Fotos | Métricas operativas ── */}
      <div className="mb-6 grid gap-4 md:grid-cols-12">
        <div className="md:col-span-4">
          <ProductoFotos productoId={p.id} />
        </div>

        <Card title="Métricas operativas" className="md:col-span-8" description={metricas.stock_fecha ? `Stock al ${fmt.date(metricas.stock_fecha)}` : 'Sin snapshots de stock.'}>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm lg:grid-cols-3">
            <Row k="Stock actual">{metricas.stock_actual != null ? fmt.int(metricas.stock_actual) : '—'}</Row>
            <Row k="Ventas L4WK">{fmt.int(metricas.ventas_l4wk)}</Row>
            <Row k="Ventas L12WK">{fmt.int(metricas.ventas_l12wk)}</Row>
            <Row k="Media semanal">{fmt.number(metricas.media_semanal_l4)}</Row>
            <Row k="Semanas stock">
              {metricas.semanas_stock == null ? '—' : (
                <span className={metricas.semanas_stock <= 20 ? 'font-bold text-rose-600' : ''}>
                  {fmt.int(metricas.semanas_stock)}
                </span>
              )}
            </Row>
            <Row k="Pedidos pendientes">{fmt.int(metricas.uds_pedidas)}</Row>
            <Row k="Target">
              {p.precio_target != null
                ? <span className="font-semibold text-emerald-700">{fmt.eur(p.precio_target)}</span>
                : <span className="text-slate-400">—</span>}
            </Row>
            <Row k="Último coste">
              {p.metricas.ultimo_coste != null
                ? <span title={p.metricas.ultimo_coste_fecha ? `Precio unidad del pedido del ${fmt.date(p.metricas.ultimo_coste_fecha)}` : 'Precio unidad del último pedido'} className="font-semibold">{fmt.eur(p.metricas.ultimo_coste)}</span>
                : <span className="text-slate-400">—</span>}
            </Row>
            <Row k="Última entrega">
              {p.metricas.ultima_entrega_fecha
                ? fmt.date(p.metricas.ultima_entrega_fecha)
                : <span className="text-slate-400">—</span>}
            </Row>
            <Row k="Creado">{fmt.date(p.fecha_creacion)}</Row>
          </dl>
        </Card>
      </div>

      {/* ── Coste de entrada (informe de coste real de recepción) ── */}
      <div className="mb-6">
        <CosteEntradaFicha productoId={p.id} clienteCodigo={clienteCodigo} clienteId={p.cliente_id ?? null} />
      </div>

      <div className="mb-6">
        <Card
          title="Ficha de producto"
          description="Características y descripción que se compartirán con clientes."
          actions={
            p.url_oficial ? (
              <Button size="sm" variant="secondary" onClick={() => setScrapeOpen(true)}>
                Importar desde URL oficial
              </Button>
            ) : null
          }
        >
          {editing
            ? <Textarea rows={6} value={(draft.ficha_descripcion as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, ficha_descripcion: e.target.value }))} placeholder="Características y descripción del producto…" />
            : (p.ficha_descripcion
              ? (/<\/?[a-z][^>]*>/i.test(p.ficha_descripcion)
                ? <div className="prose prose-sm max-w-none text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.ficha_descripcion) }} />
                : <p className="whitespace-pre-line text-sm text-slate-700">{p.ficha_descripcion}</p>)
              : <p className="text-sm italic text-slate-400">Sin ficha de producto. Pulsa &quot;Editar&quot; o &quot;Importar desde URL oficial&quot; para añadirla.</p>)}
        </Card>
      </div>

      <div className="mb-6">
        <ProductoArchivos productoId={p.id} categoriaNombre={p.categoria_nombre} />
      </div>

      <div className="mb-6">
        <HistoricoPrecioVentaSection productoId={p.id} clienteCodigo={clienteCodigo} />
      </div>

      {/* ── Histórico de ventas de APPROX al cliente (export del ERP) ── */}
      <VentasApproxFicha productoId={p.id} clienteCodigo={clienteCodigo} />

      <div className="mb-6">
        <EntregasProductoSection productoId={p.id} productoNombre={p.nombre} clienteCodigo={clienteCodigo} />
      </div>

      <div className="mb-6">
        <Card title="Pedidos">
          <DataTable<PedidoRow>
            columns={pedidoCols}
            rows={p.pedidos}
            rowKey={(r) => r.id}
            defaultSort={{ key: 'fecha', desc: true }}
            storageKey="pedidos"
          />
        </Card>
      </div>

      <ProductoScrapeModal
        productoId={p.id}
        urlOficial={p.url_oficial}
        open={scrapeOpen}
        onClose={() => setScrapeOpen(false)}
      />
      </>
      )}

      {/* ── Pestaña Cotizaciones: histórico de cotizaciones + calculadora ── */}
      {tab === 'cotizaciones' && (
        <>
          <div className="mb-6">
            <Card title="Histórico de cotizaciones" description="Cotizaciones guardadas para este producto. Cada fila es un cálculo de la calculadora de precio con todos los costes intermedios.">
              <DataTable<CotizacionRow>
                columns={cotizCols}
                rows={p.cotizaciones}
                rowKey={(r) => r.id}
                defaultSort={{ key: 'fecha', desc: true }}
                storageKey="cotiz"
              />
            </Card>
          </div>

          <div className="mb-6">
            <Card title={`Calculadora de precio ${clienteCodigo}`} description="Aplica los parámetros y guarda la cotización para este producto. Precargada con los últimos datos del producto.">
              <GameCalculadora
                embebida={{
                  producto: {
                    id: p.id,
                    gama_id: p.gama_id,
                    categoria_id: p.categoria_id,
                    subcategoria_id: p.subcategoria_id,
                    proveedor_id: p.proveedor_id,
                    ref_proveedor: p.ref_proveedor,
                  },
                  onSaved: () => qc.invalidateQueries({ queryKey: ['producto', id, 'full'] }),
                }}
              />
            </Card>
          </div>

          <ActualizarCotizacionModal
            cotizacionId={recalcCotizId}
            onClose={() => setRecalcCotizId(null)}
            onApplied={() => qc.invalidateQueries({ queryKey: ['producto', id, 'full'] })}
          />
        </>
      )}

      {/* ── Pestaña Stock y Ventas: evolución semanal | snapshots de stock ── */}
      {tab === 'stock' && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="Evolución semanal" description="Ventas y stock por semana (fecha del fichero importado). El más reciente arriba.">
            {p.historico_semanal.length === 0 ? (
              <p className="text-sm italic text-slate-400">Sin datos semanales importados para este producto.</p>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2">Semana</th>
                      <th className="px-2 py-2 text-right">Ventas semana</th>
                      <th className="px-2 py-2 text-right">Stock actual</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {p.historico_semanal.map((h) => (
                      <tr key={h.semana_inicio} className="hover:bg-slate-50">
                        <td className="px-2 py-1.5">
                          <span className="font-mono text-xs">{fmt.date(h.fecha_informe)}</span>
                          {h.ano_fiscal_game != null && h.semana_fiscal_game != null && (
                            <span className="ml-1 text-[10px] text-slate-400">· S{h.semana_fiscal_game}/{h.ano_fiscal_game}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold">{h.unidades_vendidas != null ? fmt.int(h.unidades_vendidas) : '—'}</td>
                        <td className="px-2 py-1.5 text-right">{h.stock_actual != null ? fmt.int(h.stock_actual) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="Stock — últimos snapshots">
            <DataTable<StockRow>
              columns={stockCols}
              rows={p.stock}
              rowKey={(r) => r.id}
              defaultSort={{ key: 'fecha', desc: true }}
              searchPlaceholder="Buscar en snapshots…"
              storageKey="stock"
            />
          </Card>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Modal "Actualizar cotización": recalcula UNA cotización con los datos
// vigentes (cambio, transporte por cascada, costes) y la DUPLICA en una
// cotización nueva (PDTE_DE_ENVIAR). Reusa los endpoints de recálculo masivo.
// ─────────────────────────────────────────────────────────────────────
type RecalcFuente = 'producto' | 'detalle' | 'subcategoria' | 'categoria' | 'ninguno';
type RecalcPreviewRow = {
  id: string; nombre: string | null; ref_proveedor: string | null;
  coste_usd: number; unidades: number; cambio_usd_eur: number; coste_transporte_unidad_eur: number;
  cdpe_eur: number; coste_real_eur: number; coste_game_final_eur: number; coste_game_final_old: number;
  margen_approx_real_pct: number | null;
  transporte_fuente?: RecalcFuente; precio_contenedor_eur?: number;
};
type RecalcEdit = { coste_usd?: number; unidades?: number; cambio_usd_eur?: number; coste_transporte_unidad_eur?: number };

function fuenteLabel(f: RecalcFuente | undefined): string {
  switch (f) {
    case 'producto': return 'datos del producto';
    case 'detalle': return 'detalle';
    case 'subcategoria': return 'subcategoría';
    case 'categoria': return 'categoría (fallback)';
    default: return '—';
  }
}

function ActualizarCotizacionModal({ cotizacionId, onClose, onApplied }: {
  cotizacionId: string | null;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [row, setRow] = useState<RecalcPreviewRow | null>(null);
  const [ctx, setCtx] = useState<{ cambio_usd_eur: number; precio_contenedor_eur: number } | null>(null);
  const [edit, setEdit] = useState({ coste_usd: '', unidades: '', cambio: '', transporte: '' });
  const [dirty, setDirty] = useState(false);

  const preview = useMutation({
    mutationFn: (body: { ids: string[]; overrides?: Record<string, RecalcEdit> }) =>
      api.post<{ data: RecalcPreviewRow[]; contexto: { cambio_usd_eur: number; precio_contenedor_eur: number } }>(
        '/precio/cotizaciones/bulk-actualizar/preview', body,
      ),
    onSuccess: (r) => {
      const d = r.data[0];
      setRow(d ?? null);
      setCtx(r.contexto);
      setDirty(false);
      if (d) setEdit({
        coste_usd: String(d.coste_usd), unidades: String(d.unidades),
        cambio: String(d.cambio_usd_eur), transporte: String(d.coste_transporte_unidad_eur),
      });
    },
    onError: (err) => toast.error('No se pudo calcular la previsualización', (err as Error).message),
  });

  const apply = useMutation({
    mutationFn: (body: { in_place: boolean; items: Array<{ id: string } & RecalcEdit> }) =>
      api.post<{ in_place: boolean; count: number }>('/precio/cotizaciones/bulk-actualizar', body),
    onSuccess: () => { onApplied(); onClose(); toast.success('Cotización duplicada en una nueva', 'Queda en estado Pdte. de enviar.'); },
    onError: (err) => toast.error('No se pudo duplicar la cotización', (err as Error).message),
  });

  useEffect(() => {
    if (!cotizacionId) return;
    setRow(null); setCtx(null); setDirty(false);
    setEdit({ coste_usd: '', unidades: '', cambio: '', transporte: '' });
    preview.mutate({ ids: [cotizacionId] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotizacionId]);

  const num = (s: string, fb: number) => {
    const t = (s ?? '').toString().trim();
    if (t === '') return fb;
    const n = Number(t.replace(',', '.'));
    return Number.isFinite(n) ? n : fb;
  };
  const buildItem = (): ({ id: string } & RecalcEdit) | null => {
    if (!row || !cotizacionId) return null;
    return {
      id: cotizacionId,
      coste_usd: num(edit.coste_usd, row.coste_usd),
      unidades: num(edit.unidades, row.unidades),
      cambio_usd_eur: num(edit.cambio, row.cambio_usd_eur),
      coste_transporte_unidad_eur: num(edit.transporte, row.coste_transporte_unidad_eur),
    };
  };
  const recompute = () => {
    const it = buildItem();
    if (!it || !cotizacionId) return;
    const { id: _id, ...rest } = it;
    preview.mutate({ ids: [cotizacionId], overrides: { [cotizacionId]: rest } });
  };
  const crearNueva = () => {
    const it = buildItem();
    if (!it) return;
    apply.mutate({ in_place: false, items: [it] });
  };

  const setE = (k: 'coste_usd' | 'unidades' | 'cambio' | 'transporte', v: string) => {
    setDirty(true);
    setEdit((s) => ({ ...s, [k]: v }));
  };

  const finalSubio = !!row && row.coste_game_final_old > 0 && Math.abs(row.coste_game_final_eur - row.coste_game_final_old) >= 0.005;

  return (
    <Modal
      open={!!cotizacionId}
      onClose={onClose}
      title="Actualizar cotización"
      size="lg"
      footer={
        <>
          <Button variant="danger" onClick={onClose} disabled={apply.isPending}>Cancelar</Button>
          <Button variant="secondary" onClick={recompute} disabled={preview.isPending || !row}>
            {preview.isPending ? '↻ Calculando…' : '↻ Recalcular previsualización'}
          </Button>
          <Button variant="create" onClick={crearNueva} disabled={apply.isPending || !row || dirty}>
            {apply.isPending ? 'Creando…' : '＋ Crear nueva cotización'}
          </Button>
        </>
      }
    >
      {!row ? (
        <p className="py-6 text-center text-sm text-slate-400">Calculando previsualización…</p>
      ) : (
        <div className="space-y-3 text-sm">
          {dirty && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
              Has editado valores: pulsa <strong>↻ Recalcular previsualización</strong> para actualizar costes y márgenes antes de crear.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <FormRow label="Coste fábrica USD">
              <Input type="number" step="any" value={edit.coste_usd} onChange={(e) => setE('coste_usd', e.target.value)} className="text-right font-mono" />
            </FormRow>
            <FormRow label="Unidades / MOQ">
              <Input type="number" value={edit.unidades} onChange={(e) => setE('unidades', e.target.value)} className="text-right font-mono" />
            </FormRow>
            <FormRow label="Cambio USD→EUR">
              <Input type="number" step="0.0001" value={edit.cambio} onChange={(e) => setE('cambio', e.target.value)} className="text-right font-mono" />
            </FormRow>
            <FormRow label="Transporte ud (€)">
              <Input type="number" step="any" value={edit.transporte} onChange={(e) => setE('transporte', e.target.value)} className="text-right font-mono" />
              <p className="mt-0.5 text-[10px] leading-tight text-slate-400">
                Transporte de: <span className="font-medium">{fuenteLabel(row.transporte_fuente)}</span>
                {ctx ? ` · contenedor ${fmt.eur(ctx.precio_contenedor_eur)}` : ''}
              </p>
            </FormRow>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 sm:grid-cols-4">
            <Row k="CDPe">{fmt.eur(row.cdpe_eur)}</Row>
            <Row k="Coste real">{fmt.eur(row.coste_real_eur)}</Row>
            <Row k="Coste final">
              {finalSubio && <span className="mr-1 text-[10px] text-slate-400 line-through">{fmt.eur(row.coste_game_final_old)}</span>}
              <span className="font-semibold">{fmt.eur(row.coste_game_final_eur)}</span>
            </Row>
            <Row k="Margen Approx">{row.margen_approx_real_pct != null ? `${row.margen_approx_real_pct.toFixed(1)}%` : '—'}</Row>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-900 text-right">{children}</dd>
    </div>
  );
}

// Campo en columna: etiqueta arriba, valor debajo. Para los bloques de
// identidad en rejilla (a diferencia de Row, que es etiqueta-izq/valor-dcha).
function FieldCol({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500" title={hint}>{label}</div>
      <div className="truncate text-sm text-slate-900" title={typeof children === 'string' ? children : undefined}>{children}</div>
    </div>
  );
}
